import { useReadContracts } from 'wagmi';
import { erc20Abi } from 'viem';
import { QueryOptions } from 'utils/useQueryUtils';

export interface TokenInput {
  address: `0x${string}`;
  chainId: number;
}

export interface TokenBalanceItem {
  address: `0x${string}`;
  chainId: number;
  balance: bigint;
}

export interface TokenBalancesData {
  list: TokenBalanceItem[];
  map: Record<number, Record<`0x${string}`, bigint>>;
}

export const getErc20TokenBalance = (
  data: TokenBalancesData | undefined,
  address: `0x${string}`,
  chainId: number
): bigint => data?.map[chainId]?.[address] ?? BigInt(0);

export const useErc20TokenBalances = (
  params: {
    userAddress?: `0x${string}`;
    tokens: TokenInput[];
  } & QueryOptions
) => {
  const { userAddress, tokens, query = {} } = params;

  const contracts = tokens.map((token) => ({
    address: token.address,
    abi: erc20Abi,
    functionName: 'balanceOf' as const,
    chainId: token.chainId,
    args: [userAddress] as const,
  }));

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return useReadContracts({
    contracts,
    query: {
      refetchInterval: 15000,
      staleTime: 15000,
      ...query,
      enabled: query.enabled !== false && !!userAddress && tokens.length > 0,
      select: (results): TokenBalancesData => {
        const list: TokenBalanceItem[] = [];
        const map: Record<number, Record<`0x${string}`, bigint>> = {};

        tokens.forEach((token, i) => {
          const result = results[i];
          const balance =
            result?.status === 'success' && result.result != null
              ? (result.result as bigint)
              : BigInt(0);

          list.push({
            address: token.address,
            chainId: token.chainId,
            balance,
          });

          if (!map[token.chainId]) map[token.chainId] = {};
          map[token.chainId][token.address] = balance;
        });

        return { list, map };
      },
    },
  });
};
